import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import {
  CreateSalesPriceConditionSchema,
  getConditionBindingValuesFromLevelSchema,
} from '@retail/calculus/components';
import { useConditionActions } from '@retail/calculus/context';
import { useFetchPriceSuggestionTypesWithCount } from '@retail/price-suggestion/data-access';
import { getAvailableTypes } from '@retail/price-suggestion/utils';
import { getISO_8601Date } from '@retail/utils';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import CreateSalesPriceConditionDialog from './CreateSalesPriceConditionDialog';
import { ConditionsNew } from '@retail/calculus/types';

interface Props {
  supplierNumber?: number;
  salesPriceConditions: ConditionsNew[];
  includeMarkup: boolean;
  includeLocalPriceSuggestion: boolean;
  includeUnit: boolean;
  onClose: () => void;
}

export function CreateSalesPriceCondition({
  supplierNumber,
  salesPriceConditions,
  includeMarkup,
  includeLocalPriceSuggestion,
  includeUnit,
  onClose,
}: Props) {
  const { assortmentId, appTenant, orgUnitType } = useSelectedOrgUnit();
  const { createMultipleConditions } = useConditionActions();

  const { reset, watch } = useFormContext<CreateSalesPriceConditionSchema>();

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const { data: priceSuggestionTypesWithCount = [], isLoading: isLoadingFetchPriceSuggestions } =
    useFetchPriceSuggestionTypesWithCount(
      {
        assortmentId,
        mgSupplierId: supplierNumber,
        productGroup: (watch('productGroup') ?? watch('mainGroup') ?? watch('overGroup'))
          ?.groupNumber,
        mgItemNumber: watch('item')?.mgItemNumber,
      },
      { suspense: false }
    );

  const filteredPriceSuggestions = useMemo(() => {
    const availableTypes = getAvailableTypes({
      countryCode: appTenant.countryCode,
      orgUnitType,
      canReadLocal: includeLocalPriceSuggestion,
      assortmentLevel: !supplierNumber,
    });
    return priceSuggestionTypesWithCount.filter((type) =>
      availableTypes.includes(type.suggestionType)
    );
  }, [
    appTenant.countryCode,
    orgUnitType,
    priceSuggestionTypesWithCount,
    supplierNumber,
    includeLocalPriceSuggestion,
  ]);

  const submitSalesPriceConditions = useCallback(
    (data: CreateSalesPriceConditionSchema) => {
      const bindingValue = getConditionBindingValuesFromLevelSchema({
        levelSchema: data,
        supplierNumber,
      });
      const markupContent =
        data.markupCoverageDegree != null
          ? {
              markupCoverageDegree: { coverageDegree: data.markupCoverageDegree },
            }
          : undefined;

      const markups = markupContent
        ? bindingValue.map((value) => {
            return {
              content: markupContent,
              binding: value,
              validFrom: getISO_8601Date(data.fromDate),
            };
          })
        : [];
      const priceSource = bindingValue.map((value) => {
        return {
          content: {
            priceSuggestion: {
              priorities: data.priceSourcePriority.includes('MARKUP')
                ? data.priceSourcePriority
                : [...(data.priceSourcePriority ?? []), 'MARKUP'],
            },
          },
          binding: value,
          validFrom: getISO_8601Date(data.fromDate),
        };
      });
      createMultipleConditions([...markups, ...priceSource]);
      handleClose();
    },
    [createMultipleConditions, handleClose, supplierNumber]
  );

  return (
    <CreateSalesPriceConditionDialog
      open
      salesPriceConditions={salesPriceConditions}
      supplierNumber={supplierNumber}
      filteredPriceSuggestions={filteredPriceSuggestions}
      includeMarkup={includeMarkup}
      includeUnit={includeUnit}
      isLoadingFetchPriceSuggestions={isLoadingFetchPriceSuggestions}
      submitConditions={submitSalesPriceConditions}
      onClose={handleClose}
    />
  );
}
